import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { use, useId, useRef } from "react";
import { classes } from "./classes.js";
import { HelpFieldLabelContext, mergedRefOf, setNativeChecked, setNativeValue, useHelpControl } from "./help-control.js";
export function Field({ label, description, error, children }) {
    const id = useId();
    const descriptionId = `${id}-description`;
    const errorId = `${id}-error`;
    const describedBy = classes(description !== undefined && descriptionId, error !== undefined && errorId);
    return (_jsxs("div", { className: "field", children: [_jsx("label", { htmlFor: id, children: label }), _jsx(HelpFieldLabelContext, { value: label, children: children({
                    id,
                    "aria-describedby": describedBy === "" ? undefined : describedBy,
                    "aria-invalid": error === undefined ? undefined : true,
                }) }), description !== undefined ? (_jsx("span", { id: descriptionId, className: "wt-field-description", children: description })) : null, error !== undefined ? (_jsx("span", { id: errorId, className: "wt-field-error", role: "alert", children: error })) : null] }));
}
/** The fill kind an input of `type` is, or `null` for one that is never a target by kind. */
function inputKindOf(type) {
    switch ((type ?? "text").toLowerCase()) {
        case "text":
            return "text";
        case "number":
            return "whole_number";
        case "time":
            return "local_time";
        case "date":
            return "local_date";
        case "checkbox":
            return "toggle";
        default:
            return null;
    }
}
export function Input({ font = "body", helpNever, ref, ...rest }) {
    const own = useRef(null);
    const fieldLabel = use(HelpFieldLabelContext);
    const kind = inputKindOf(rest.type);
    const mark = useHelpControl({
        name: rest.name,
        label: rest["aria-label"] ?? fieldLabel ?? rest.placeholder ?? "",
        type: rest.type,
        autoComplete: rest.autoComplete,
        disabled: rest.disabled,
        readOnly: rest.readOnly,
        never: helpNever === true || kind === null,
    }, () => kind === null
        ? null
        : {
            kind,
            ...(rest.maxLength === undefined ? {} : { maxLength: rest.maxLength }),
            read: () => {
                const element = own.current;
                if (element === null)
                    return null;
                if (kind === "toggle")
                    return element.checked;
                if (element.value === "")
                    return null;
                return kind === "whole_number" ? Number(element.value) : element.value;
            },
            set: (value) => {
                if (kind === "toggle")
                    setNativeChecked(own.current, value === true);
                else
                    setNativeValue(own.current, String(value));
            },
        });
    return _jsx("input", { className: classes("input", font === "mono" && "wt-mono"), ...rest, ...mark, ref: mergedRefOf(own, ref) });
}
export function Textarea(props) {
    return _jsx("textarea", { className: "input", ...props });
}
export function Select({ helpNever, ref, ...props }) {
    const own = useRef(null);
    const fieldLabel = use(HelpFieldLabelContext);
    const mark = useHelpControl({
        name: props.name,
        label: props["aria-label"] ?? fieldLabel ?? "",
        autoComplete: props.autoComplete,
        disabled: props.disabled,
        never: helpNever === true || props.multiple === true,
    }, () => {
        const element = own.current;
        // The options as the page draws them now (a select's children are the form's own).
        const option = element === null ? [] : [...element.options].filter((one) => !one.disabled && one.value !== "").map((one) => ({ value: one.value, label: (one.textContent ?? one.value).trim() }));
        return {
            kind: "single_choice",
            option,
            read: () => (own.current === null || own.current.value === "" ? null : own.current.value),
            set: (value) => {
                setNativeValue(own.current, String(value));
            },
        };
    });
    return _jsx("select", { className: "input", ...props, ...mark, ref: mergedRefOf(own, ref) });
}
export function Radio({ label, ...rest }) {
    return (_jsxs("label", { className: "radio", children: [_jsx("input", { type: "radio", ...rest }), _jsx("span", { className: "dot", "aria-hidden": "true" }), _jsx("span", { children: label })] }));
}
/**
 * Broadsheet's `.seg` — a segmented control built from native radios.
 *
 * The markup is exactly what `styles.css` expects: `.seg > label.seg-opt > input + span`.
 * Broadsheet already hides the input (`.seg-opt input { position:absolute; opacity:0 }`)
 * and draws the selected state with `.seg-opt:has(input:checked)`, so there is no
 * "selected" class to add and none is added. `.seg` is `display:inline-flex`, which a
 * `<fieldset>`'s `<legend>` would sit inside as a flex item — hence `role="radiogroup"`
 * on a plain element and the group name on `aria-label`.
 */
export function Segmented({ name, legend, options, value, onChange, helpNever, }) {
    // ADR 0072: the group is one fill target, set through its own onChange.
    const mark = useHelpControl({ name, label: legend, never: helpNever }, () => ({
        kind: "single_choice",
        option: options.map((one) => ({ value: one.value, label: one.label })),
        read: () => value,
        set: (next) => {
            const found = options.find((one) => one.value === next);
            if (found !== undefined)
                onChange(found.value);
        },
    }));
    return (_jsx("div", { className: "seg", role: "radiogroup", "aria-label": legend, ...mark, children: options.map((option) => (_jsxs("label", { className: "seg-opt", children: [_jsx("input", { type: "radio", name: name, value: option.value, checked: option.value === value, onChange: () => {
                        onChange(option.value);
                    } }), _jsx("span", { children: option.label })] }, option.value))) }));
}
//# sourceMappingURL=form.js.map